import { Controller } from "react-hook-form";
import { Plus } from "lucide-react";
import { Select } from "../ui";
import { openModal } from "../../_app/entityModals/store";

/**
 * Dedupes what used to be an identical FormInputSingleSelect "Market" block
 * hand-written in both CustomerDetails and PackageDetails. `onChange`
 * reports the full market object (not just its id) - callers need
 * `market.currency[0].sign` for price display.
 *
 * `trigger` (RHF's, passed down from the hoisted form) is called explicitly
 * after a selection - Select's trigger button doesn't reliably fire a
 * native blur when an option is picked (the dropdown just unmounts, and
 * click-driven focus behavior varies by browser), so `mode: "onBlur"`
 * never re-validates this field on selection and a "Market is required."
 * error shown before the first pick stays on screen after a valid one.
 *
 * "+ Add new market" uses Select's `footerAction` (a persistent row pinned
 * below the scrollable option list) rather than a plain entry in `options`,
 * matching CompanySelect's own always-visible "+ Create new company"
 * button - an entry appended to `options` got buried (and effectively
 * undiscoverable) below however many real markets existed, requiring a
 * scroll past ~9 to even find it in Invoice/Subscription testing.
 * The created market shows correctly via `selectedLabel` even before
 * `createMarket`'s "Markets" tag invalidation refetches this list's own
 * query (every `markets` prop caller here uses `useGetAllMarketsByOrgIdQuery`,
 * which is subscribed org-wide - no manual refetch wiring needed).
 */
const MarketSelect = ({ control, name = "market", markets = [], label = "Market", rules, trigger }) => {
  const options = markets.map((market) => ({ label: market.marketName, value: market._id }));

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <Select
          id={name}
          label={label}
          options={options}
          value={field.value?._id}
          selectedLabel={field.value?.marketName}
          onChange={(value) => {
            const selected = markets.find((market) => market._id === value) || null;
            field.onChange(selected);
            trigger?.(name);
          }}
          onBlur={field.onBlur}
          error={fieldState.error?.message}
          footerAction={{
            label: "Add new market",
            icon: Plus,
            onClick: async () => {
              const created = await openModal("create_market");
              if (created) {
                field.onChange(created);
                trigger?.(name);
              }
            },
          }}
        />
      )}
    />
  );
};

export default MarketSelect;
